/**
 * ELITE homepage — modular GSAP architecture
 * Native document scroll only. No wheel hijack. No Lenis.
 *
 * Motion pass v3 — cinematic hierarchy + Theme Editor safe lifecycle.
 */
(function () {
  'use strict';

  const PRELOAD = 'elite-preloading';
  const READY = 'elite-ready';
  const SAFETY_MS = 2400;
  const FAILSAFE_MS = 2800;
  /** @type {Map<string, {destroy: Function}>} */
  const registry = new Map();
  let gsapPromise = null;
  let booted = false;
  let refreshQueued = false;
  let refreshTimer = null;
  let motionReady = false;
  /** @type {Array<() => void>} */
  const pendingReveals = [];

  document.documentElement.classList.add('elite-js');

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  function unlockScroll() {
    document.documentElement.classList.remove(PRELOAD);
    document.documentElement.classList.add(READY);
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    document.documentElement.style.opacity = '';
    document.body.style.opacity = '';
  }

  function eliteScroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper && window.matchMedia('(min-width: 990px)').matches) return wrapper;
    return undefined;
  }

  function configureScrollTrigger(ScrollTrigger) {
    if (!ScrollTrigger) return;
    const scroller = eliteScroller();
    if (scroller) {
      ScrollTrigger.defaults({ scroller });
    } else {
      ScrollTrigger.defaults({ scroller: undefined });
    }
  }

  function loadGsap() {
    if (gsapPromise) return gsapPromise;
    if (window.gsap && window.ScrollTrigger) {
      window.gsap.registerPlugin(window.ScrollTrigger);
      configureScrollTrigger(window.ScrollTrigger);
      gsapPromise = Promise.resolve({ gsap: window.gsap, ScrollTrigger: window.ScrollTrigger });
      return gsapPromise;
    }

    gsapPromise = new Promise((resolve) => {
      const finish = () => {
        if (window.gsap && window.ScrollTrigger) {
          window.gsap.registerPlugin(window.ScrollTrigger);
          configureScrollTrigger(window.ScrollTrigger);
          resolve({ gsap: window.gsap, ScrollTrigger: window.ScrollTrigger });
        } else if (window.gsap) {
          resolve({ gsap: window.gsap, ScrollTrigger: null });
        } else {
          resolve({ gsap: null, ScrollTrigger: null });
        }
      };

      if (window.gsap) return finish();

      const g = document.createElement('script');
      g.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js';
      g.async = true;
      g.onload = () => {
        const s = document.createElement('script');
        s.src = 'https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js';
        s.async = true;
        s.onload = finish;
        s.onerror = finish;
        document.head.appendChild(s);
      };
      g.onerror = finish;
      document.head.appendChild(g);
    });

    return gsapPromise;
  }

  function refreshEliteScroll() {
    if (!window.ScrollTrigger) return;
    if (refreshQueued) return;
    refreshQueued = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        refreshQueued = false;
        try {
          window.ScrollTrigger.refresh();
        } catch (e) {
          /* ignore */
        }
      });
    });
  }

  function scheduleRefresh(delay) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refreshEliteScroll(), delay || 0);
  }

  function waitForImage(img) {
    if (!img) return Promise.resolve();
    if (img.complete && img.naturalWidth > 0) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => resolve();
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      window.setTimeout(done, 1800);
    });
  }

  function waitForEliteLayout() {
    const fontsReady =
      document.fonts && document.fonts.ready
        ? document.fonts.ready.catch(() => {})
        : Promise.resolve();

    const imgs = Array.from(
      document.querySelectorAll(
        '[data-elite-fw-hero] img, [data-elite-category] img, [data-elite-bs-bar] img, [data-elite-campaign] img'
      )
    );

    return Promise.all([fontsReady, ...imgs.map(waitForImage)]);
  }

  function waitForIntro() {
    if (!document.querySelector('[data-elite-intro]')) return Promise.resolve();
    if (document.documentElement.classList.contains(READY)) return Promise.resolve();
    return new Promise((resolve) => {
      document.addEventListener('elite:intro-complete', () => resolve(), { once: true });
      window.setTimeout(resolve, SAFETY_MS + 400);
    });
  }

  function isNearViewport(el, offsetRatio) {
    if (!el) return false;
    const rect = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const offset = (offsetRatio || 0.25) * vh;
    return rect.top < vh + offset && rect.bottom > -offset;
  }

  function clearInlineHide(els) {
    els.forEach((el) => {
      if (!el) return;
      el.style.opacity = '';
      el.style.transform = '';
      el.style.clipPath = '';
      el.style.visibility = '';
      el.style.width = '';
      el.style.letterSpacing = '';
      el.removeAttribute('data-elite-pending-reveal');
    });
  }

  function armReveal(section, key, hide, animate) {
    if (!section) return;
    const id = `${key}-${section.id}`;
    if (registry.has(id)) return;

    let played = false;
    let st = null;
    let ctx = null;
    let failsafe = null;

    const play = (gsap) => {
      if (played) return;
      played = true;
      if (failsafe) window.clearTimeout(failsafe);
      try {
        animate(gsap);
      } catch (e) {
        clearInlineHide(section.querySelectorAll('[data-elite-pending-reveal]'));
      }
      section.dataset.eliteRevealed = 'true';
    };

    loadGsap().then(({ gsap, ScrollTrigger }) => {
      if (!gsap || !ScrollTrigger || reduced()) {
        clearInlineHide(section.querySelectorAll('[data-elite-pending-reveal]'));
        section.dataset.eliteRevealed = 'true';
        section.classList.add('is-inview');
        return;
      }

      ctx = gsap.context(() => {
        hide(gsap);

        st = ScrollTrigger.create({
          trigger: section,
          start: 'top 85%',
          once: true,
          invalidateOnRefresh: true,
          onEnter: () => play(gsap),
        });

        if (isNearViewport(section, 0.35) || (st && st.progress > 0)) {
          play(gsap);
        }
      }, section);

      failsafe = window.setTimeout(() => {
        if (!played) {
          clearInlineHide(section.querySelectorAll('[data-elite-pending-reveal]'));
          if (gsap) {
            gsap.set(section.querySelectorAll('[data-elite-pending-reveal]'), {
              clearProps: 'opacity,transform,clipPath,visibility,width,y,yPercent,letterSpacing',
            });
          }
          section.dataset.eliteRevealed = 'true';
          section.classList.add('is-inview');
          played = true;
        }
      }, FAILSAFE_MS);

      pendingReveals.push(() => {
        if (!played && isNearViewport(section, 0.4)) play(gsap);
      });
    });

    registry.set(id, {
      destroy() {
        if (failsafe) window.clearTimeout(failsafe);
        st?.kill();
        ctx?.revert();
        registry.delete(id);
      },
    });
  }

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  /* ——— INTRO ——— */
  function runIntro() {
    const intro = document.querySelector('[data-elite-intro]');
    const designMode = Boolean(window.Shopify?.designMode);
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      intro?.classList.add('is-done');
      unlockScroll();
      document.dispatchEvent(new CustomEvent('elite:intro-complete'));
      window.setTimeout(() => intro?.remove(), designMode ? 0 : 600);
      scheduleRefresh(80);
    };

    if (!intro || reduced() || designMode) {
      finish();
      return;
    }

    document.documentElement.classList.add(PRELOAD);
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    const safety = window.setTimeout(finish, SAFETY_MS);

    loadGsap().then(({ gsap }) => {
      if (!gsap) {
        window.clearTimeout(safety);
        window.setTimeout(finish, 700);
        return;
      }
      const letters = intro.querySelectorAll('.elite-intro__mark span');
      const meta = intro.querySelector('.elite-intro__meta');
      const line = intro.querySelector('[data-elite-intro-line]');
      const mark = intro.querySelector('.elite-intro__mark');
      gsap
        .timeline({
          onComplete: () => {
            window.clearTimeout(safety);
            window.setTimeout(finish, 120);
          },
        })
        .to(letters, { opacity: 1, y: 0, duration: 0.5, stagger: 0.05, ease: 'power3.out' })
        .to(meta, { opacity: 1, duration: 0.3 }, '-=0.15')
        .to(line, { scaleX: 1, duration: 0.5, ease: 'power3.inOut' }, '-=0.1')
        .to(mark, { scale: 0.96, duration: 0.35, ease: 'power2.inOut' }, '+=0.08')
        .to(intro, { opacity: 0, duration: 0.4, ease: 'power2.inOut' }, '-=0.1');
    });
  }

  /* ——— HEADER ——— */
  /* Chrome sticky system lives in elite-chrome.js — avoid duplicate listeners. */
  function initEliteHeader() {
    return;
  }

  /* ——— HERO ——— */
  function initEliteHero(section) {
    if (!section || section.dataset.eliteInitialized === 'true') return;
    section.dataset.eliteInitialized = 'true';

    const slides = Array.from(section.querySelectorAll('[data-elite-fw-slide]'));
    if (!slides.length) return;

    const holdMs = Number(section.dataset.hold || 5500);
    const transitionMs = Number(section.dataset.transition || 1000);
    const progress = section.querySelector('[data-elite-fw-progress]');
    const indexNum = section.querySelector('[data-elite-fw-index-num]');
    const indexTotal = section.querySelector('[data-elite-fw-index-total]');
    const prevBtn = section.querySelector('[data-elite-fw-prev]');
    const nextBtn = section.querySelector('[data-elite-fw-next]');
    const content = section.querySelector('[data-elite-fw-content]');
    const shade = section.querySelector('.elite-fw-hero__shade');
    const total = slides.length;
    let index = 0;
    let busy = false;
    let holdTimer = null;
    let raf = null;
    let started = false;
    let destroyed = false;
    let gsap = null;
    let ctx = null;
    let indexBusy = false;

    if (indexTotal) indexTotal.textContent = ` / ${pad2(total)}`;

    function stopHold() {
      if (holdTimer) window.clearTimeout(holdTimer);
      holdTimer = null;
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    }

    function startProgress() {
      if (!progress) return;
      // Instant reset at transition boundary
      progress.style.transition = 'none';
      progress.style.width = '0%';
      // Force reflow so next fill starts clean
      void progress.offsetWidth;
      const start = performance.now();
      const tick = (now) => {
        if (destroyed || busy) return;
        const p = Math.min(1, (now - start) / holdMs);
        progress.style.width = `${p * 100}%`;
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }

    function schedule() {
      stopHold();
      startProgress();
      holdTimer = window.setTimeout(() => goTo((index + 1) % total), holdMs);
    }

    function animateIndex(next) {
      if (!indexNum) return;
      const label = pad2(next + 1);
      if (!gsap || reduced() || indexBusy) {
        indexNum.textContent = label;
        return;
      }
      indexBusy = true;
      gsap
        .timeline({
          onComplete: () => {
            indexBusy = false;
          },
        })
        .to(indexNum, { y: -5, opacity: 0, duration: 0.18, ease: 'power2.in' })
        .add(() => {
          indexNum.textContent = label;
        })
        .fromTo(indexNum, { y: 5, opacity: 0 }, { y: 0, opacity: 1, duration: 0.2, ease: 'power2.out' });
    }

    function brightnessTint(slide) {
      if (!shade || !gsap || reduced()) return;
      const img = slide.querySelector('img');
      // Soft local readability shift — no visible gradient wash
      const darkish = img?.dataset?.eliteTone === 'dark';
      gsap.to(shade, {
        opacity: darkish ? 0.42 : 0.34,
        duration: 0.85,
        ease: 'power2.inOut',
        overwrite: true,
      });
    }

    function goTo(next) {
      if (busy || destroyed || next === index) return;
      busy = true;
      stopHold();
      if (progress) {
        progress.style.transition = 'none';
        progress.style.width = '0%';
      }
      const prev = index;
      const outgoing = slides[prev];
      const incoming = slides[next];
      animateIndex(next);
      brightnessTint(incoming);

      const duration = Math.min(1.1, Math.max(0.9, transitionMs / 1000)) * (reduced() ? 0.35 : 1);

      const complete = () => {
        slides.forEach((el, i) => {
          el.classList.toggle('is-active', i === next);
          el.setAttribute('aria-hidden', i === next ? 'false' : 'true');
          if (gsap) {
            gsap.set(el, {
              opacity: i === next ? 1 : 0,
              visibility: i === next ? 'visible' : 'hidden',
              scale: 1,
              y: 0,
              clipPath: 'inset(0% 0 0 0)',
              zIndex: i === next ? 1 : 0,
              clearProps: i === next ? '' : 'willChange',
            });
          }
        });
        index = next;
        busy = false;
        if (started) schedule();
      };

      if (!gsap || reduced()) {
        if (gsap) {
          gsap.set(incoming, { visibility: 'visible', opacity: 0 });
          gsap.to(outgoing, { opacity: 0, duration: duration * 0.7 });
          gsap.to(incoming, { opacity: 1, duration: duration * 0.7, onComplete: complete });
        } else {
          outgoing.classList.remove('is-active');
          incoming.classList.add('is-active');
          complete();
        }
        return;
      }

      gsap.set(incoming, {
        opacity: 0,
        visibility: 'visible',
        zIndex: 3,
        scale: 1.035,
        y: 14,
        clipPath: 'inset(100% 0 0 0)',
        willChange: 'transform, opacity, clip-path',
      });
      gsap.set(outgoing, { zIndex: 2, willChange: 'transform, opacity' });

      const tl = gsap.timeline({
        onComplete: complete,
        defaults: { ease: 'power3.inOut', duration },
      });
      tl.to(outgoing, { opacity: 0, scale: 1.025, y: -10 }, 0).to(
        incoming,
        { opacity: 1, scale: 1, y: 0, clipPath: 'inset(0% 0 0 0)' },
        duration * 0.18
      );

      if (content) {
        gsap.fromTo(
          content,
          { opacity: 1, y: 0 },
          { opacity: 0.92, y: -2, duration: 0.2, yoyo: true, repeat: 1, ease: 'power1.inOut' }
        );
      }
    }

    function entrance() {
      const reveals = section.querySelectorAll('[data-elite-fw-reveal]');
      if (!gsap || reduced()) {
        reveals.forEach((el) => {
          el.style.opacity = '1';
          el.style.transform = 'none';
        });
        return Promise.resolve();
      }

      return new Promise((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          resolve();
        };

        ctx = gsap.context(() => {
          const media = section.querySelector('[data-elite-fw-media]');
          const eyebrow = section.querySelector('[data-elite-fw-reveal="eyebrow"]');
          const lines = section.querySelectorAll('.elite-fw-hero__line > span');
          const cta = section.querySelector('[data-elite-fw-reveal="cta"]');
          const meta = section.querySelector('[data-elite-fw-reveal="meta"]');
          const controls = section.querySelector('[data-elite-fw-reveal="controls"]');
          gsap.set(media, { clipPath: 'inset(100% 0 0 0)', willChange: 'clip-path' });
          gsap.set([eyebrow, cta, meta, controls].filter(Boolean), { opacity: 0, y: 8 });
          gsap.set(lines, { yPercent: 110 });

          const tl = gsap.timeline({
            defaults: { ease: 'power3.out' },
            onComplete: () => {
              if (media) gsap.set(media, { clearProps: 'willChange' });
              done();
            },
          });
          // Header entrance is owned by elite-chrome.js
          tl.to(media, { clipPath: 'inset(0% 0 0 0)', duration: 0.95 }, 0.05)
            .to(eyebrow, { opacity: 1, y: 0, duration: 0.4 }, 0.28)
            .to(lines, { yPercent: 0, duration: 0.7, stagger: 0.08 }, 0.35)
            .to(cta, { opacity: 1, y: 0, duration: 0.42 }, 0.72)
            .to(meta, { opacity: 1, y: 0, duration: 0.35 }, 0.95)
            .to(controls, { opacity: 1, y: 0, duration: 0.35 }, 1.05);

          window.setTimeout(() => {
            reveals.forEach((el) => {
              if (getComputedStyle(el).opacity === '0') {
                gsap.set(el, { clearProps: 'opacity,transform' });
                el.style.opacity = '1';
              }
            });
            if (media) gsap.set(media, { clipPath: 'inset(0% 0 0 0)', clearProps: 'willChange' });
            done();
          }, 1700);
        }, section);
      });
    }

    function start() {
      if (started || destroyed) return;
      started = true;
      entrance().finally(() => {
        if (!destroyed) schedule();
        scheduleRefresh(50);
      });
    }

    const onPrev = () => goTo((index - 1 + total) % total);
    const onNext = () => goTo((index + 1) % total);
    prevBtn?.addEventListener('click', onPrev);
    nextBtn?.addEventListener('click', onNext);

    const boot = () => {
      loadGsap().then((lib) => {
        gsap = lib.gsap;
        Promise.all(Array.from(section.querySelectorAll('img')).map(waitForImage)).then(start);
      });
    };

    if (document.documentElement.classList.contains(READY) || !document.querySelector('[data-elite-intro]')) {
      boot();
    } else {
      document.addEventListener('elite:intro-complete', boot, { once: true });
    }

    const api = {
      destroy() {
        destroyed = true;
        stopHold();
        prevBtn?.removeEventListener('click', onPrev);
        nextBtn?.removeEventListener('click', onNext);
        ctx?.revert();
        section.dataset.eliteInitialized = 'false';
        registry.delete(section.id);
      },
    };
    registry.set(section.id, api);
    return api;
  }

  /* ——— CATEGORY (T-Shirts / Pants) ——— */
  function initEliteCategory(section) {
    if (!section || section.dataset.eliteInitialized === 'true') return;
    section.dataset.eliteInitialized = 'true';

    const layout = section.dataset.layout || 'image_left';
    const isPants = layout === 'image_right';
    const media = section.querySelector('[data-elite-cat-media]');
    const parallax = section.querySelector('[data-elite-cat-parallax]');
    const copy = section.querySelector('[data-elite-cat-copy]');
    const line = section.querySelector('[data-elite-cat-line]');
    const indexEl = section.querySelector('[data-elite-cat-index]');
    const titleInner = section.querySelector('.elite-cat__title-line > span');
    const meta = section.querySelector('[data-elite-cat-meta]');
    const cta = section.querySelector('[data-elite-cat-cta]');

    [media, copy, indexEl, titleInner, line, meta, cta].forEach((el) =>
      el?.setAttribute('data-elite-pending-reveal', '')
    );

    const showStatic = () => {
      clearInlineHide([media, copy, indexEl, titleInner, line, meta, cta]);
      section.classList.add('is-inview');
    };

    if (reduced()) {
      showStatic();
      return;
    }

    armReveal(
      section,
      'cat',
      (gsap) => {
        if (isPants) {
          gsap.set(media, { clipPath: 'inset(0 0 0 100%)', scale: 1.04, y: 25 });
          gsap.set(copy, { opacity: 0, x: -18 });
        } else {
          gsap.set(media, { clipPath: 'inset(100% 0 0 0)', scale: 1.04, y: 25 });
          gsap.set(copy, { opacity: 1 });
        }
        gsap.set(indexEl, { opacity: 0, y: 8 });
        gsap.set(titleInner, { yPercent: 110 });
        gsap.set(line, { width: 0 });
        gsap.set([meta, cta].filter(Boolean), { opacity: 0, y: 6 });
      },
      (gsap) => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
        tl.to(media, {
          clipPath: 'inset(0% 0 0 0)',
          scale: 1,
          y: 0,
          duration: 0.95,
        })
          .to(indexEl, { opacity: 1, y: 0, duration: 0.4 }, 0.12)
          .to(titleInner, { yPercent: 0, duration: 0.65 }, 0.2)
          .to(line, { width: 64, duration: 0.5 }, 0.32)
          .to(meta, { opacity: 1, y: 0, duration: 0.4 }, 0.4)
          .to(cta, { opacity: 1, y: 0, duration: 0.4 }, 0.5);

        if (isPants) {
          tl.to(copy, { opacity: 1, x: 0, duration: 0.55 }, 0.15);
        }
        section.classList.add('is-inview');
      }
    );

    const scrubId = `cat-scrub-${section.id}`;
    loadGsap().then(({ gsap, ScrollTrigger }) => {
      if (!gsap || !ScrollTrigger || reduced() || isMobile()) return;
      const ctx = gsap.context(() => {
        if (parallax) {
          gsap.fromTo(
            parallax,
            { y: 0 },
            {
              y: isPants ? -25 : -35,
              ease: 'none',
              scrollTrigger: {
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
                invalidateOnRefresh: true,
              },
            }
          );
        }
        if (copy) {
          gsap.fromTo(
            copy,
            { y: 0 },
            {
              y: isPants ? 15 : 12,
              ease: 'none',
              scrollTrigger: {
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
                invalidateOnRefresh: true,
              },
            }
          );
        }
      }, section);
      registry.set(scrubId, {
        destroy() {
          ctx.revert();
          registry.delete(scrubId);
        },
      });
    });

    registry.set(section.id, {
      destroy() {
        registry.get(`cat-${section.id}`)?.destroy();
        registry.get(scrubId)?.destroy();
        section.dataset.eliteInitialized = 'false';
        registry.delete(section.id);
      },
    });
  }

  /* ——— BRANDS ——— */
  function initEliteBrands(section) {
    if (!section || section.dataset.eliteInitialized === 'true') return;
    section.dataset.eliteInitialized = 'true';

    const rows = Array.from(section.querySelectorAll('[data-elite-brand-row]'));
    const visuals = Array.from(section.querySelectorAll('[data-elite-brand-visual-item]'));
    const visualRoot = section.querySelector('[data-elite-brand-visual]');
    let active = 0;
    let pointerRaf = null;
    let targetX = 0;
    let targetY = 0;
    let currentX = 0;
    let currentY = 0;
    let gsapLib = null;

    loadGsap().then(({ gsap }) => {
      gsapLib = gsap;
      if (gsap && visuals[0] && !reduced()) {
        gsap.set(visuals, { opacity: 0, clipPath: 'inset(0 100% 0 0)', scale: 1.03 });
        gsap.set(visuals[0], { opacity: 1, clipPath: 'inset(0 0 0 0)', scale: 1 });
      }
    });

    function show(i) {
      if (i === active && rows[i]?.classList.contains('is-active')) {
        // still sync mobile
      }
      const prev = active;
      active = i;
      rows.forEach((row, idx) => row.classList.toggle('is-active', idx === i));

      if (isMobile()) {
        visuals.forEach((v, idx) => v.classList.toggle('is-active', idx === i));
        section.querySelectorAll('[data-elite-brand-mobile]').forEach((el) => {
          const idx = Number(el.getAttribute('data-elite-brand-mobile'));
          el.hidden = idx !== i;
        });
        return;
      }

      if (!gsapLib || reduced()) {
        visuals.forEach((v, idx) => v.classList.toggle('is-active', idx === i));
        return;
      }

      const outgoing = visuals[prev];
      const incoming = visuals[i];
      if (!incoming || prev === i) {
        visuals.forEach((v, idx) => v.classList.toggle('is-active', idx === i));
        return;
      }

      visuals.forEach((v, idx) => v.classList.toggle('is-active', idx === i));
      gsapLib.set(incoming, {
        opacity: 0,
        clipPath: 'inset(0 100% 0 0)',
        scale: 1.03,
        zIndex: 2,
        willChange: 'clip-path, opacity, transform',
      });
      if (outgoing) gsapLib.set(outgoing, { zIndex: 1 });

      gsapLib
        .timeline({
          defaults: { ease: 'power3.out', duration: 0.55 },
          onComplete: () => {
            if (outgoing && prev !== i) {
              gsapLib.set(outgoing, {
                opacity: 0,
                clipPath: 'inset(0 100% 0 0)',
                scale: 1.03,
                clearProps: 'willChange',
              });
            }
            gsapLib.set(incoming, { clearProps: 'willChange' });
          },
        })
        .to(outgoing, { opacity: 0, scale: 1.02, duration: 0.4 }, 0)
        .to(incoming, { opacity: 1, clipPath: 'inset(0 0 0 0)', scale: 1 }, 0.04);
    }

    const onEnter = (e) => {
      const i = Number(e.currentTarget.dataset.brandIndex);
      if (!Number.isNaN(i)) show(i);
    };

    const onClick = (e) => {
      if (!isMobile()) return;
      const row = e.currentTarget;
      const i = Number(row.dataset.brandIndex);
      const mobile = section.querySelector(`[data-elite-brand-mobile="${i}"]`);
      if (mobile && mobile.hidden === false && row.classList.contains('is-active')) return;
      e.preventDefault();
      show(i);
    };

    rows.forEach((row) => {
      row.addEventListener('mouseenter', onEnter);
      row.addEventListener('focus', onEnter);
      row.addEventListener('click', onClick);
    });

    // Desktop pointer lag (8–12px max)
    const tickPointer = () => {
      currentX += (targetX - currentX) * 0.12;
      currentY += (targetY - currentY) * 0.12;
      if (visualRoot) {
        visualRoot.style.transform = `translate3d(${currentX.toFixed(2)}px, ${currentY.toFixed(2)}px, 0)`;
      }
      if (Math.abs(targetX - currentX) > 0.05 || Math.abs(targetY - currentY) > 0.05) {
        pointerRaf = requestAnimationFrame(tickPointer);
      } else {
        pointerRaf = null;
      }
    };

    const onPointerMove = (e) => {
      if (isMobile() || reduced() || !visualRoot) return;
      const rect = visualRoot.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      targetX = Math.max(-10, Math.min(10, nx * 10));
      targetY = Math.max(-8, Math.min(8, ny * 8));
      if (!pointerRaf) pointerRaf = requestAnimationFrame(tickPointer);
    };

    const onPointerLeave = () => {
      targetX = 0;
      targetY = 0;
      if (!pointerRaf) pointerRaf = requestAnimationFrame(tickPointer);
    };

    section.addEventListener('pointermove', onPointerMove);
    section.addEventListener('pointerleave', onPointerLeave);

    show(0);

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => scheduleRefresh(60));
      ro.observe(section);
    }

    registry.set(section.id, {
      destroy() {
        rows.forEach((row) => {
          row.removeEventListener('mouseenter', onEnter);
          row.removeEventListener('focus', onEnter);
          row.removeEventListener('click', onClick);
        });
        section.removeEventListener('pointermove', onPointerMove);
        section.removeEventListener('pointerleave', onPointerLeave);
        if (pointerRaf) cancelAnimationFrame(pointerRaf);
        ro?.disconnect();
        section.dataset.eliteInitialized = 'false';
        registry.delete(section.id);
      },
    });
  }

  /* ——— BEST SELLERS BAR ——— */
  function initEliteBestSellersBar(section) {
    if (!section || section.dataset.eliteInitialized === 'true') return;
    section.dataset.eliteInitialized = 'true';

    const rail = section.querySelector('[data-elite-bs-rail]');
    const prevBtn = section.querySelector('[data-elite-bs-prev]');
    const nextBtn = section.querySelector('[data-elite-bs-next]');
    const indexEl = section.querySelector('[data-elite-bs-index]');
    const items = Array.from(section.querySelectorAll('[data-elite-bs-item]'));
    if (!rail || !items.length) return;

    let current = 0;
    let animatingIndex = false;

    function stepWidth() {
      const first = items[0];
      if (!first) return 260;
      const style = window.getComputedStyle(rail);
      const gap = parseFloat(style.columnGap || style.gap || '22') || 22;
      const w = first.getBoundingClientRect().width;
      return (w > 0 ? w : 240) + gap;
    }

    function updateIndex(next) {
      const clamped = Math.max(0, Math.min(items.length - 1, next));
      if (clamped !== current) {
        const previous = current;
        current = clamped;
        if (indexEl) {
          if (reduced() || !window.gsap) {
            indexEl.textContent = pad2(current + 1);
          } else if (!animatingIndex) {
            animatingIndex = true;
            const gsap = window.gsap;
            const dir = current >= previous ? -1 : 1;
            gsap
              .timeline({
                onComplete: () => {
                  animatingIndex = false;
                },
              })
              .to(indexEl, { y: -6 * dir, opacity: 0, duration: 0.12, ease: 'power1.in' })
              .add(() => {
                indexEl.textContent = pad2(current + 1);
              })
              .fromTo(indexEl, { y: 6 * dir, opacity: 0 }, { y: 0, opacity: 1, duration: 0.16, ease: 'power1.out' });
          } else {
            indexEl.textContent = pad2(current + 1);
          }
        }
      }

      if (prevBtn) prevBtn.disabled = rail.scrollLeft <= 4;
      if (nextBtn) nextBtn.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 4;
    }

    function syncFromScroll() {
      const w = stepWidth();
      updateIndex(Math.round(rail.scrollLeft / w));
    }

    function scrollByDir(dir) {
      rail.scrollBy({ left: stepWidth() * dir, behavior: 'smooth' });
      window.setTimeout(syncFromScroll, 360);
    }

    const onPrev = () => scrollByDir(-1);
    const onNext = () => scrollByDir(1);
    prevBtn?.addEventListener('click', onPrev);
    nextBtn?.addEventListener('click', onNext);
    rail.addEventListener('scroll', syncFromScroll, { passive: true });

    let dragging = false;
    let startX = 0;
    let startLeft = 0;
    const onPointerDown = (e) => {
      if (isMobile()) return;
      dragging = true;
      startX = e.clientX;
      startLeft = rail.scrollLeft;
      rail.classList.add('is-dragging');
    };
    const onPointerMove = (e) => {
      if (!dragging) return;
      rail.scrollLeft = startLeft - (e.clientX - startX);
    };
    const onPointerUp = () => {
      dragging = false;
      rail.classList.remove('is-dragging');
      syncFromScroll();
    };
    rail.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);

    const revealNodes = section.querySelectorAll('[data-elite-bs-reveal]');

    function armBestSellersReveal() {
      if (section.dataset.eliteBsRevealArmed === 'true') return;
      section.dataset.eliteBsRevealArmed = 'true';
      revealNodes.forEach((el) => el.setAttribute('data-elite-pending-reveal', ''));

      if (reduced()) {
        clearInlineHide(revealNodes);
        return;
      }

      armReveal(
        section,
        'bs',
        (gsap) => {
          const label = section.querySelector('[data-elite-bs-reveal="label"]');
          const headline = section.querySelector('[data-elite-bs-reveal="headline"] > span');
          const line = section.querySelector('[data-elite-bs-reveal="line"]');
          const view = section.querySelector('[data-elite-bs-reveal="view"]');
          const index = section.querySelector('[data-elite-bs-reveal="index"]');
          const nav = section.querySelector('[data-elite-bs-reveal="nav"]');
          const cards = section.querySelectorAll('[data-elite-bs-reveal="card"]');
          gsap.set([label, view, index, nav].filter(Boolean), { opacity: 0, y: 6 });
          if (headline) gsap.set(headline, { yPercent: 110 });
          if (line) gsap.set(line, { width: 0 });
          gsap.set(cards, { opacity: 0, y: 18 });
        },
        (gsap) => {
          const label = section.querySelector('[data-elite-bs-reveal="label"]');
          const headline = section.querySelector('[data-elite-bs-reveal="headline"] > span');
          const line = section.querySelector('[data-elite-bs-reveal="line"]');
          const view = section.querySelector('[data-elite-bs-reveal="view"]');
          const index = section.querySelector('[data-elite-bs-reveal="index"]');
          const nav = section.querySelector('[data-elite-bs-reveal="nav"]');
          const cards = section.querySelectorAll('[data-elite-bs-reveal="card"]');
          const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
          if (label) tl.to(label, { opacity: 1, y: 0, duration: 0.35 }, 0);
          if (headline) tl.to(headline, { yPercent: 0, duration: 0.55 }, 0.05);
          if (line) tl.to(line, { width: 220, duration: 0.45 }, 0.2);
          if (view) tl.to(view, { opacity: 1, y: 0, duration: 0.35 }, 0.15);
          if (index) tl.to(index, { opacity: 1, y: 0, duration: 0.35 }, 0.25);
          if (nav) tl.to(nav, { opacity: 1, y: 0, duration: 0.35 }, 0.3);
          tl.to(cards, { opacity: 1, y: 0, duration: 0.45, stagger: 0.08 }, 0.2);
        }
      );
    }

    if (motionReady) {
      armBestSellersReveal();
    } else {
      document.addEventListener('elite:motion-ready', armBestSellersReveal, { once: true });
    }

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => {
        syncFromScroll();
        scheduleRefresh(80);
      });
      ro.observe(rail);
    }

    syncFromScroll();

    registry.set(section.id, {
      destroy() {
        prevBtn?.removeEventListener('click', onPrev);
        nextBtn?.removeEventListener('click', onNext);
        rail.removeEventListener('scroll', syncFromScroll);
        rail.removeEventListener('pointerdown', onPointerDown);
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        ro?.disconnect();
        registry.get(`bs-${section.id}`)?.destroy();
        section.dataset.eliteInitialized = 'false';
        section.dataset.eliteBsRevealArmed = 'false';
        registry.delete(section.id);
      },
    });
  }

  /* ——— PRODUCTS REVEAL (legacy selected edit) ——— */
  function initEliteProducts(section) {
    if (!section || section.dataset.eliteInitialized === 'true') return;
    section.dataset.eliteInitialized = 'true';
    const cards = section.querySelectorAll('[data-elite-product]');
    if (!cards.length) return;

    const revealAll = () => cards.forEach((c) => c.classList.add('is-inview'));

    if (reduced() || !('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-inview');
          io.unobserve(entry.target);
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px 10% 0px' }
    );
    cards.forEach((c, i) => {
      c.style.transitionDelay = `${i * 80}ms`;
      io.observe(c);
      if (isNearViewport(c, 0.2)) c.classList.add('is-inview');
    });

    window.setTimeout(() => {
      cards.forEach((c) => {
        if (!c.classList.contains('is-inview')) c.classList.add('is-inview');
      });
    }, FAILSAFE_MS);

    registry.set(`prod-${section.id}`, {
      destroy() {
        io.disconnect();
        section.dataset.eliteInitialized = 'false';
        registry.delete(`prod-${section.id}`);
      },
    });
  }

  /* ——— CAMPAIGN PEAK ——— */
  function initEliteCampaign(section) {
    if (!section || section.dataset.eliteInitialized === 'true') return;
    section.dataset.eliteInitialized = 'true';

    loadGsap().then(({ gsap, ScrollTrigger }) => {
      if (!gsap || !ScrollTrigger) return;

      const frames = Array.from(section.querySelectorAll('[data-elite-campaign-frame]'));
      const masks = Array.from(section.querySelectorAll('[data-elite-campaign-mask]'));
      const captions = Array.from(section.querySelectorAll('[data-elite-campaign-caption]'));
      const maskDirs = [
        'inset(100% 0 0 0)', // bottom → top
        'inset(0 100% 0 0)', // left → right
        'inset(0 0 100% 0)', // top → bottom
        'inset(0 0 0 100%)', // right → left
      ];

      // Mobile: calm vertical reveals, one scale moment
      if (reduced() || isMobile()) {
        const ctx = gsap.context(() => {
          frames.forEach((frame, i) => {
            const mask = frame.querySelector('[data-elite-campaign-mask]');
            const caption = frame.querySelector('[data-elite-campaign-caption]');
            if (mask) gsap.set(mask, { clipPath: maskDirs[i % maskDirs.length], opacity: 0.85 });
            if (caption) gsap.set(caption, { opacity: 0, letterSpacing: '5px' });

            ScrollTrigger.create({
              trigger: frame,
              start: 'top 88%',
              once: true,
              onEnter: () => {
                if (mask) {
                  gsap.to(mask, {
                    clipPath: 'inset(0% 0 0 0)',
                    opacity: 1,
                    duration: 0.7,
                    ease: 'power3.out',
                  });
                }
                if (i === 0) {
                  gsap.fromTo(frame, { scale: 0.96 }, { scale: 1, duration: 0.8, ease: 'power3.out' });
                }
                if (caption) {
                  gsap.to(caption, {
                    opacity: 1,
                    letterSpacing: '2px',
                    duration: 0.5,
                    ease: 'power2.out',
                    delay: 0.15,
                  });
                }
              },
            });
          });
        }, section);

        registry.set(section.id, {
          destroy() {
            ctx.revert();
            section.dataset.eliteInitialized = 'false';
            registry.delete(section.id);
          },
        });
        scheduleRefresh(60);
        return;
      }

      const ctx = gsap.context(() => {
        const stage = section.querySelector('[data-elite-campaign-stage]');
        const hero = section.querySelector('[data-role="hero"], .elite-campaign__frame--1');
        const side = section.querySelector('[data-role="side"], .elite-campaign__frame--2');
        const narrow = section.querySelector('[data-role="narrow"], .elite-campaign__frame--3');
        const frame = section.querySelector('[data-role="frame"], .elite-campaign__frame--4');
        const word = section.querySelector('[data-elite-campaign-word]');
        const line = section.querySelector('[data-elite-campaign-line]');
        const bg = section.querySelector('[data-elite-campaign-bg]');

        masks.forEach((mask, i) => {
          gsap.set(mask, { clipPath: maskDirs[i % maskDirs.length] });
        });
        captions.forEach((cap) => gsap.set(cap, { opacity: 0, letterSpacing: '5px' }));
        if (hero) gsap.set(hero, { width: '45vw', y: 80, scale: 0.86, xPercent: -50, left: '50%' });
        if (side) gsap.set(side, { y: 120, opacity: 0.55 });
        if (narrow) gsap.set(narrow, { y: 180, opacity: 0.55 });
        if (frame) gsap.set(frame, { y: 150, opacity: 0.55 });
        if (word) gsap.set(word, { y: 60, opacity: 0.25, zIndex: 1 });

        const tl = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: stage,
            start: 'top 75%',
            end: 'bottom bottom',
            scrub: true,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              const p = self.progress;
              section.classList.toggle('is-dark', p > 0.35 && p < 0.82);
              section.classList.toggle('is-peak', p > 0.48 && p < 0.68);
            },
          },
        });

        // Stage 1–2: depth rise + mask reveals
        masks.forEach((mask, i) => {
          tl.to(mask, { clipPath: 'inset(0% 0 0 0)', duration: 0.18 }, i * 0.04);
        });
        captions.forEach((cap, i) => {
          tl.to(cap, { opacity: 1, letterSpacing: '2px', duration: 0.12 }, 0.1 + i * 0.05);
        });

        if (hero) {
          tl.to(hero, { scale: 1, y: -20, duration: 0.35 }, 0);
        }
        if (side) tl.to(side, { y: -80, opacity: 1, duration: 0.4 }, 0.05);
        if (narrow) tl.to(narrow, { y: -40, opacity: 1, duration: 0.4 }, 0.08);
        if (frame) tl.to(frame, { y: -100, opacity: 1, duration: 0.42 }, 0.1);

        // Stage 3: typography behind frames
        if (word) {
          tl.to(word, { y: -40, opacity: 1, duration: 0.3 }, 0.22);
        }

        // Stage 4: expand hero pause
        if (hero) {
          tl.to(hero, { width: '86vw', y: -10, duration: 0.28 }, 0.42);
        }
        if (side) tl.to(side, { opacity: 0.32, duration: 0.2 }, 0.45);
        if (narrow) tl.to(narrow, { opacity: 0.28, duration: 0.2 }, 0.45);
        if (frame) tl.to(frame, { opacity: 0.3, duration: 0.2 }, 0.45);
        if (bg) tl.to(bg, { backgroundColor: '#050505', duration: 0.2 }, 0.45);

        // Stage 5: settle + rearrange
        if (hero) tl.to(hero, { width: '55vw', y: -30, duration: 0.28 }, 0.7);
        if (side) tl.to(side, { opacity: 0.85, y: -100, duration: 0.25 }, 0.72);
        if (narrow) tl.to(narrow, { opacity: 0.85, y: -70, duration: 0.25 }, 0.74);
        if (frame) tl.to(frame, { opacity: 0.9, y: -120, duration: 0.25 }, 0.76);
        if (word) tl.to(word, { opacity: 0.35, y: -70, duration: 0.2 }, 0.75);

        if (line) {
          tl.fromTo(line, { width: 0 }, { width: '70%', duration: 0.35 }, 0.2);
        }
      }, section);

      let ro = null;
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => scheduleRefresh(100));
        ro.observe(section);
      }

      registry.set(section.id, {
        destroy() {
          ctx.revert();
          ro?.disconnect();
          section.dataset.eliteInitialized = 'false';
          section.classList.remove('is-peak', 'is-dark');
          registry.delete(section.id);
        },
      });

      scheduleRefresh(60);
    });
  }

  /* ——— TRUST ——— */
  function initEliteTrust(section) {
    if (!section || section.dataset.eliteInitialized === 'true') return;
    section.dataset.eliteInitialized = 'true';
    const items = Array.from(section.querySelectorAll('[data-elite-trust-item]'));
    const line = section.querySelector('[data-elite-trust-line]');
    const dots = Array.from(section.querySelectorAll('.elite-trust-strip__dot'));
    if (!items.length) return;

    items.forEach((el) => el.setAttribute('data-elite-pending-reveal', ''));
    if (line) line.setAttribute('data-elite-pending-reveal', '');

    if (reduced()) {
      clearInlineHide([...items, line]);
      section.classList.add('is-inview');
      return;
    }

    armReveal(
      section,
      'trust',
      (gsap) => {
        gsap.set(items, { opacity: 0, y: 8 });
        if (line) gsap.set(line, { width: 0 });
        if (dots.length) gsap.set(dots, { scale: 0 });
      },
      (gsap) => {
        const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
        if (line) tl.to(line, { width: '100%', duration: 0.55, ease: 'power3.inOut' }, 0);
        tl.to(items, { opacity: 1, y: 0, duration: 0.45, stagger: 0.08 }, 0.1);
        if (dots.length) tl.to(dots, { scale: 1, duration: 0.35, stagger: 0.08 }, 0.15);
        section.classList.add('is-inview');
      }
    );

    registry.set(section.id, {
      destroy() {
        registry.get(`trust-${section.id}`)?.destroy();
        section.dataset.eliteInitialized = 'false';
        registry.delete(section.id);
      },
    });
  }

  /* ——— FINAL CTA ——— */
  function initEliteFinal(section) {
    if (!section || section.dataset.eliteInitialized === 'true') return;
    section.dataset.eliteInitialized = 'true';

    const media = section.querySelector('[data-elite-final-media]');
    const indexEl = section.querySelector('[data-elite-final-index]');
    const lines = section.querySelectorAll('.elite-final__line > span');
    const cta = section.querySelector('[data-elite-final-cta]');

    [media, indexEl, cta, ...lines].forEach((el) => el?.setAttribute('data-elite-pending-reveal', ''));

    if (reduced()) {
      clearInlineHide([media, indexEl, cta, ...lines]);
      return;
    }

    armReveal(
      section,
      'final',
      (gsap) => {
        if (media) gsap.set(media, { scale: 1.06 });
        gsap.set(indexEl, { opacity: 0 });
        gsap.set(lines, { yPercent: 110 });
        if (cta) gsap.set(cta, { clipPath: 'inset(100% 0 0 0)', opacity: 0 });
      },
      (gsap) => {
        const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
        if (media) tl.to(media, { scale: 1, duration: 1.1 }, 0);
        tl.to(indexEl, { opacity: 1, duration: 0.4 }, 0.15)
          .to(lines, { yPercent: 0, duration: 0.7, stagger: 0.1 }, 0.2)
          .to(cta, { clipPath: 'inset(0% 0 0 0)', opacity: 1, duration: 0.5 }, 0.55);
      }
    );

    const scrubId = `final-scrub-${section.id}`;
    loadGsap().then(({ gsap, ScrollTrigger }) => {
      if (!gsap || !ScrollTrigger || reduced() || isMobile()) return;
      const ctx = gsap.context(() => {
        if (media) {
          gsap.to(media, {
            y: -25,
            ease: 'none',
            scrollTrigger: {
              trigger: section,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
              invalidateOnRefresh: true,
            },
          });
        }
        const title = section.querySelector('[data-elite-final-title]');
        if (title) {
          gsap.to(title, {
            y: 12,
            ease: 'none',
            scrollTrigger: {
              trigger: section,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
              invalidateOnRefresh: true,
            },
          });
        }
      }, section);
      registry.set(scrubId, {
        destroy() {
          ctx.revert();
          registry.delete(scrubId);
        },
      });
    });

    registry.set(section.id, {
      destroy() {
        registry.get(`final-${section.id}`)?.destroy();
        registry.get(scrubId)?.destroy();
        section.dataset.eliteInitialized = 'false';
        registry.delete(section.id);
      },
    });
  }

  /* ——— FOOTER ——— */
  function initEliteFooter(section) {
    if (!section || section.dataset.eliteInitialized === 'true') return;
    section.dataset.eliteInitialized = 'true';

    const brand = section.querySelector('[data-elite-footer-brand]');
    const cols = section.querySelector('[data-elite-footer-cols]');
    const line = section.querySelector('[data-elite-footer-line]');
    const bottom = section.querySelector('[data-elite-footer-bottom]');
    const colChildren = cols ? Array.from(cols.children) : [];

    [brand, ...colChildren, line, bottom].forEach((el) => el?.setAttribute('data-elite-pending-reveal', ''));

    if (reduced()) {
      clearInlineHide([brand, ...colChildren, line, bottom]);
      return;
    }

    armReveal(
      section,
      'footer',
      (gsap) => {
        gsap.set(brand, { opacity: 0 });
        gsap.set(colChildren, { opacity: 0, y: 8 });
        if (line) gsap.set(line, { width: 0 });
        gsap.set(bottom, { opacity: 0 });
      },
      (gsap) => {
        const tl = gsap.timeline({ defaults: { ease: 'power2.out' } });
        tl.to(brand, { opacity: 1, duration: 0.4 }, 0)
          .to(colChildren, { opacity: 1, y: 0, duration: 0.4, stagger: 0.08 }, 0.1)
          .to(line, { width: '100%', duration: 0.55, ease: 'power3.inOut' }, 0.2)
          .to(bottom, { opacity: 1, duration: 0.35 }, 0.35);
      }
    );

    registry.set(section.id, {
      destroy() {
        registry.get(`footer-${section.id}`)?.destroy();
        section.dataset.eliteInitialized = 'false';
        registry.delete(section.id);
      },
    });
  }

  function syncCartCount() {
    const badges = document.querySelectorAll('[data-elite-cart-count]');
    if (!badges.length) return;
    const update = (count) => {
      badges.forEach((badge) => {
        const n = Number(count) || 0;
        badge.textContent = n > 99 ? '99+' : String(n);
        badge.classList.toggle('is-empty', n === 0);
      });
    };
    document.addEventListener('cart:update', (event) => {
      const d = event.detail || {};
      const count = d.resource?.item_count ?? d.cart?.item_count ?? d.itemCount;
      if (count != null) update(count);
    });
  }

  function initInteractive(root) {
    if (!(root instanceof HTMLElement)) return;
    if (root.matches('[data-elite-fw-hero]')) initEliteHero(root);
    if (root.matches('[data-elite-brands]')) initEliteBrands(root);
    if (root.matches('[data-elite-bs-bar]')) initEliteBestSellersBar(root);
    if (root.matches('[data-elite-products], [data-elite-selected]')) initEliteProducts(root);
    if (root.matches('[data-elite-header], [data-elite-header-root]')) initEliteHeader(root);

    root
      .querySelectorAll?.(
        '[data-elite-fw-hero], [data-elite-brands], [data-elite-bs-bar], [data-elite-products], [data-elite-selected], [data-elite-header], [data-elite-header-root]'
      )
      .forEach((el) => {
        if (el !== root) initInteractive(el);
      });
  }

  function initMotionSections(root) {
    const run = (el) => {
      if (!(el instanceof HTMLElement)) return;
      if (el.matches('[data-elite-category]')) initEliteCategory(el);
      if (el.matches('[data-elite-campaign]')) initEliteCampaign(el);
      if (el.matches('[data-elite-trust]')) initEliteTrust(el);
      if (el.matches('[data-elite-final]')) initEliteFinal(el);
      if (el.matches('[data-elite-footer]')) initEliteFooter(el);
    };

    if (!(root instanceof HTMLElement)) {
      document
        .querySelectorAll(
          '[data-elite-category], [data-elite-campaign], [data-elite-trust], [data-elite-final], [data-elite-footer]'
        )
        .forEach(run);
      return;
    }

    run(root);
    root
      .querySelectorAll?.(
        '[data-elite-category], [data-elite-campaign], [data-elite-trust], [data-elite-final], [data-elite-footer]'
      )
      .forEach(run);
  }

  function destroyIn(root) {
    if (!(root instanceof HTMLElement)) return;
    const ids = [];
    root.querySelectorAll('[id]').forEach((el) => {
      if (registry.has(el.id)) ids.push(el.id);
      [
        `prod-${el.id}`,
        `trust-${el.id}`,
        `cat-${el.id}`,
        `bs-${el.id}`,
        `cat-scrub-${el.id}`,
        `final-${el.id}`,
        `final-scrub-${el.id}`,
        `footer-${el.id}`,
        `header-${el.id}`,
      ].forEach((k) => {
        if (registry.has(k)) ids.push(k);
      });
      el.dataset.eliteInitialized = 'false';
      el.dataset.eliteRevealed = 'false';
    });
    if (registry.has(root.id)) ids.push(root.id);
    root.dataset.eliteInitialized = 'false';
    [...new Set(ids)].forEach((id) => registry.get(id)?.destroy());
  }

  function forceVisibleFailsafe() {
    document
      .querySelectorAll(
        '[data-elite-pending-reveal], [data-elite-bs-reveal], [data-elite-trust-item], .elite-cat__media, .elite-cat__copy, .elite-cat__title, [data-elite-final-content], [data-elite-footer]'
      )
      .forEach((el) => {
        const opacity = parseFloat(getComputedStyle(el).opacity);
        if (opacity < 0.05) {
          el.style.opacity = '1';
          el.style.transform = 'none';
          el.style.clipPath = 'none';
          el.style.visibility = 'visible';
        }
      });
    document.querySelectorAll('[data-elite-product]').forEach((c) => c.classList.add('is-inview'));
    unlockScroll();
  }

  async function startMotionPipeline() {
    await waitForIntro();
    await waitForEliteLayout();
    await loadGsap();

    document.querySelectorAll('[data-elite-category]').forEach(initEliteCategory);
    document.querySelectorAll('[data-elite-campaign]').forEach(initEliteCampaign);
    document.querySelectorAll('[data-elite-trust]').forEach(initEliteTrust);
    document.querySelectorAll('[data-elite-final]').forEach(initEliteFinal);
    document.querySelectorAll('[data-elite-footer]').forEach(initEliteFooter);

    motionReady = true;
    document.documentElement.classList.add('elite-motion-ready');
    document.dispatchEvent(new CustomEvent('elite:motion-ready'));
    refreshEliteScroll();
    pendingReveals.forEach((fn) => fn());
    scheduleRefresh(120);
    scheduleRefresh(480);

    window.setTimeout(() => {
      refreshEliteScroll();
      pendingReveals.forEach((fn) => fn());
      forceVisibleFailsafe();
    }, FAILSAFE_MS);
  }

  function bootstrap() {
    if (window.__ELITE_HOME_V3 || booted) return;
    window.__ELITE_HOME_V3 = true;
    booted = true;
    document.body.classList.add('elite-site');
    syncCartCount();
    initEliteHeader();

    if (document.querySelector('[data-elite-intro], [data-elite-fw-hero]')) {
      runIntro();
    } else {
      unlockScroll();
    }

    document
      .querySelectorAll(
        '[data-elite-fw-hero], [data-elite-brands], [data-elite-bs-bar], [data-elite-products], [data-elite-selected]'
      )
      .forEach((el) => initInteractive(el));

    startMotionPipeline();

    const main = document.getElementById('MainContent');
    if (main && typeof ResizeObserver !== 'undefined') {
      let lastW = main.clientWidth;
      const ro = new ResizeObserver(() => {
        const w = main.clientWidth;
        if (Math.abs(w - lastW) > 2) {
          lastW = w;
          scheduleRefresh(100);
        }
      });
      ro.observe(main);
    }

    // Absolute failsafe against leftover intro locks
    window.setTimeout(unlockScroll, SAFETY_MS + 800);
  }

  document.addEventListener('DOMContentLoaded', bootstrap);
  if (document.readyState !== 'loading') bootstrap();

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    initInteractive(event.target);
    initMotionSections(event.target);
    if (event.target.matches('[data-elite-header], [data-elite-header-root]') || event.target.querySelector('[data-elite-header]')) {
      initEliteHeader(event.target.querySelector('[data-elite-header]') || event.target);
    }
    waitForEliteLayout().then(() => {
      refreshEliteScroll();
      scheduleRefresh(150);
    });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target instanceof HTMLElement) destroyIn(event.target);
  });

  document.addEventListener('shopify:section:reorder', () => {
    registry.forEach((api) => api.destroy?.());
    document.querySelectorAll('[id]').forEach((el) => {
      if (el.dataset) {
        el.dataset.eliteInitialized = 'false';
        el.dataset.eliteRevealed = 'false';
        el.dataset.eliteBsRevealArmed = 'false';
      }
    });
    motionReady = false;
    document.documentElement.classList.remove('elite-motion-ready');
    initEliteHeader();
    document
      .querySelectorAll('[data-elite-fw-hero], [data-elite-brands], [data-elite-bs-bar], [data-elite-products]')
      .forEach(initInteractive);
    startMotionPipeline();
  });
})();
